import { Add, AutoAwesomeOutlined, InfoRounded, Loop } from '@mui/icons-material'
import { Box, Button, CircularProgress, Skeleton, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import React, { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { postGenerateSkillSuggestion } from 'services/api/modules/recruitment/job-advertisment'
import palette from 'theme/palette'

/**
 * Renders a skill recommender component that generates recommended skills using ChatGPT based on job title, description, and requirement.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {(value: { id: number, name: string }) => void} props.handleSetSelectedSkills - A callback function to handle the selection of skills.
 * @param {{ position_title: string, job_description: string, job_requirement: string }} props.formData - The form data containing the job title, description, and requirement.
 * @param {number} [props.skillsTotal] - The total number of skills (i.e. existing skill list plus number of custom skills).
 * @param {boolean} [props.disabled=false] - A flag indicating if the component is disabled.
 * @return {JSX.Element} The rendered skill recommender component.
 */
const SkillRecommender = ({
  handleSetSelectedSkills,
  formData,
  skillsTotal = 0,
  disabled = false,
  isEdit,
}) => {
  const {
    data: skillSuggestions,
    mutateAsync: generateSkillSuggestion,
    isLoading: isGenerating,
    isError: isGenerateError,
  } = useMutation('skillSuggestions', async (formData) => {
    const { data } = await postGenerateSkillSuggestion(formData)
    return data
  })

  // Decided to use a state variable instead of skillSuggestions directly to allow removing selected skills
  const [mappedSkills, setMappedSkills] = useState([])
  useEffect(() => {
    if (!skillSuggestions || skillSuggestions?.length === 0) return
    setMappedSkills([])
    let runningTotal = skillsTotal
    const mapped = skillSuggestions?.map((item) => {
      runningTotal += 1
      return {
        id: runningTotal,
        name: item,
        custom: true,
      }
    })
    setMappedSkills((prev) => [...prev, ...mapped])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillSuggestions])

  /**
   * Prompts ChatGPT to generate skill suggestions based on the job title, description, and requirement.
   *
   * @return {Promise<void>}
   */
  const handleGenerate = async () => {
    await generateSkillSuggestion({
      job_title: formData.position_title,
      job_description: formData.job_description,
      job_requirement: formData.job_requirement,
    })
  }

  /**
   * Handles the selection of skills.
   *
   * @param {{ id: number, name: string }} value - The selected skill.
   * @return {void}
   */
  const handleSelectSkills = (value) => {
    const filteredSkills = mappedSkills?.filter((item) => item.name !== value.name)
    setMappedSkills(filteredSkills)
    handleSetSelectedSkills(value)
  }

  return (
    <Box>
      <Box
        sx={{
          minHeight: '56.5px',
          background: 'linear-gradient(to right, #EF6617, #F3BF3A, #F27C19)',
          py: '10px',
          px: '14px',
          borderRadius: disabled ? '5px 5px 0 0' : '5px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Title and Icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: '36.5px' }}>
            {isGenerating ? (
              <>
                <CircularProgress size={18} htmlColor="#FFFFFF" />
                <Typography sx={{ color: 'text.white' }}>
                  {mappedSkills?.length > 0
                    ? 'Regenerating results..'
                    : 'Generating recommended skills..'}
                </Typography>
              </>
            ) : (
              <>
                {isGenerateError ? (
                  <InfoRounded htmlColor="#FFFFFF" sx={{ transform: 'rotate(180deg)' }} />
                ) : (
                  <AutoAwesomeOutlined htmlColor="#FFFFFF" />
                )}
                <Typography sx={{ color: 'text.white' }}>
                  {isGenerateError
                    ? 'Failed to generate skills'
                    : mappedSkills?.length > 0
                    ? 'Recommended skills from AI'
                    : 'Generate recommended skills with AI'}
                </Typography>
              </>
            )}
          </Box>

          {/* Generate button */}
          {!skillSuggestions || skillSuggestions?.length === 0
            ? !isGenerating && (
                <AdxButton
                  variant="contained"
                  size={'tiny'}
                  onClick={handleGenerate}
                  disabled={disabled}
                  sx={{ bgcolor: 'background.main' }}
                >
                  {isGenerateError && <Loop sx={{ mr: 1, transform: 'rotate(90deg)' }} />}
                  {disabled ? 'Unavailable' : isGenerateError ? 'Regenerate' : 'Generate Skills'}
                </AdxButton>
              )
            : !isGenerating && (
                <AdxButton
                  variant={'contained'}
                  size={'tiny'}
                  onClick={handleGenerate}
                  disabled={disabled}
                  sx={{ bgcolor: 'background.main' }}
                >
                  <Loop sx={{ mr: 1, transform: 'rotate(90deg)' }} />
                  Regenerate
                </AdxButton>
              )}
        </Box>

        {/* List of generated skills */}
        {mappedSkills?.length > 0 && (
          <Box
            sx={{
              mt: '16px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            {mappedSkills?.map((item, index) =>
              isGenerating ? (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '10px',
                    width: '130px',
                    height: '35px',
                    px: '14px',
                    py: '7px',
                    borderRadius: '5px',
                    backgroundColor: 'white.main',
                  }}
                >
                  <Skeleton height={'14px'} width={'77px'} variant="rectangular" />
                  <Skeleton height={'14px'} width={'14px'} variant="rectangular" />
                </Box>
              ) : (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    px: '14px',
                    py: '7px',
                    backgroundColor: 'white.main',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'gray-line.main',
                    },
                  }}
                  onClick={() => handleSelectSkills(item)}
                >
                  <Typography sx={{ color: 'alternate.main' }}>{item.name}</Typography>
                  <Add sx={{ color: 'alternate.main', height: '16px', width: '16px' }} />
                </Box>
              )
            )}
          </Box>
        )}
      </Box>
      {disabled && !isEdit && (
        <Box
          sx={{
            px: '14px',
            py: '12px',
            borderWidth: '1px',
            borderColor: 'gray-line.main',
            borderStyle: 'solid',
            borderRadius: '0 0 5px 5px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <InfoRounded
            sx={{
              color: 'warning.main',
              transform: 'rotate(180deg)',
            }}
          />
          To use this feature, please complete your job description and requirement
        </Box>
      )}
    </Box>
  )
}

export default SkillRecommender
